import axios, { CancelTokenSource } from 'axios'
import {
  debounce as debounceFn,
  DebounceSettings,
  isNil,
  throttle as throttleFn,
  ThrottleSettings,
} from 'lodash'
import { onUnmounted, reactive, Ref, toRefs, watch } from '@vue/composition-api'

import {
  RequestError,
  ResponseData,
  UseRequestConfig,
  UseRequestState,
} from '@shared/types/request'
import { PromiseDataType } from '@shared/types/utils'
import { useDocumentVisibility } from '@shared/use/use-document-visibility'
import {
  OptionalParamsRequest,
  RequiredParamsRequest,
} from '@shared/utils/http'
import { YttFlag } from '@shared/utils/ytt-request'

import {
  getCache,
  getCacheByPending,
  removeCache,
  runCacheQueue,
  setCacheData,
  setCachePending,
} from './cache'

export type RequestFunc<T extends object = any> =
  | RequiredParamsRequest<T>
  | OptionalParamsRequest<T>
type Params<T extends RequestFunc> = Parameters<T>[0]
type RefreshDeps = boolean | Ref<any> | Ref<any>[] | (() => any)

type IsParamsRequired<T extends RequestFunc> = T extends RequiredParamsRequest<
  any
>
  ? {
      params: Params<T> | (() => Params<T>)
    }
  : {
      params?: Params<T> | (() => Params<T>)
    }

type CacheOptions = Partial<{
  cacheKey: any
  cacheTime: number
  staleTime: number
}>
export interface RequestOptions<T, R, U extends RequestFunc> {
  refreshDeps?: RefreshDeps
  refreshOptions?: {
    deep: boolean
  }
  refreshWhen?: () => boolean
  fetchWhen?: () => boolean
  initialData?: Partial<T>
  config?: UseRequestConfig
  catcher?: boolean
  manual?: boolean
  onSuccess?: (response: ResponseData<R>, params: Params<U>) => void
  onError?: (error: RequestError, params: Params<U>) => void
  onEnd?: () => void
  formatResult?: (
    response: ResponseData<R>,
    params: Params<U>,
    prevData: T
  ) => T
  debounce?:
    | number
    | {
        wait: number
        options: DebounceSettings
      }
  throttle?:
    | number
    | {
        wait: number
        options: ThrottleSettings
      }
  loadingDelay?: number
  polling?:
    | number
    | {
        interval?: number
        stopWhenHidden?: boolean
      }
}

const normalFn = <T extends (...args: any[]) => any>(
  func: T,
  state: UseRequestState
) => {
  let cancelToken: CancelTokenSource
  const cancel = () => {
    if (cancelToken) {
      cancelToken.cancel('useRequest called cancel')
    }
  }

  const fn = (params: any) => {
    if (state.loading) {
      // cancel last
      cancel()
    }

    // generate new CancelToken
    cancelToken = axios.CancelToken.source()
    return func(params, cancelToken.token)
  }

  fn.cancel = cancel
  return fn
}

const normalizeScheduler = <T>(
  scheduler: number | { wait: number; options: T }
): [number, T] => {
  if (typeof scheduler === 'number') {
    return [scheduler, {} as T]
  }

  return [scheduler.wait, scheduler.options]
}

const enum NeverFlag {
  _ = '',
}
type GetYttFunReturnData<YttFun extends RequestFunc> = PromiseDataType<
  ReturnType<YttFun>
> extends ResponseData<infer W>
  ? W
  : unknown
type GetOriginData<
  ROrYttFun,
  YttFun extends RequestFunc
> = ROrYttFun extends NeverFlag
  ? GetYttFunReturnData<YttFun>
  : ROrYttFun extends RequestFunc
  ? GetYttFunReturnData<ROrYttFun>
  : ROrYttFun

export const useRequest = <
  T = NeverFlag, // 格式化后的类型，NeverFlag 是为了推断的时候知道这个 T 有没有传
  R extends any = T, // 服务端返回的原始数据类型，或者是请求函数的类型
  U extends RequestFunc = RequestFunc, // 请求函数的类型，如果 R 传了，这里就不用了
  OriginData = GetOriginData<R, U>, // 根据前面的信息推断出来的原始数据类型，不用传
  FormattedData = U extends YttFlag ? (T extends NeverFlag ? OriginData : T) : T // 根据前面的信息推断出来的格式化后的数据类型，不用传
>(
  requestFunc: U,
  options: RequestOptions<
    FormattedData,
    OriginData,
    R extends RequestFunc ? R : U
  > &
    CacheOptions &
    IsParamsRequired<R extends RequestFunc ? R : U> = {} as any
) => {
  const {
    params,
    refreshDeps,
    initialData,
    config = {},
    catcher = true,
    manual = false,
    onSuccess,
    onError,
    onEnd,
    formatResult,
    debounce,
    throttle,
    loadingDelay,
    polling,
    refreshWhen,
    cacheKey,
    cacheTime = 300000,
    staleTime = 0,
  } = options

  if (refreshDeps === true && typeof params !== 'function') {
    throw new Error(`"refreshDeps" 为 true 时, 'params' 必须为函数`)
  }

  const state = reactive<UseRequestState<FormattedData>>({
    data: initialData as FormattedData,
    error: undefined,
    loading: false,
    code: 0,
    msg: '',
  })

  const visible = useDocumentVisibility()

  let loadingDelayTimer = -1 as any
  let pollingTimer = -1 as any

  const clearLoadingDelayTimer = () => {
    clearTimeout(loadingDelayTimer)
    loadingDelayTimer = -1
  }

  const {
    interval: pollingInterval,
    stopWhenHidden: pollingStopWhenHidden = true,
  } = typeof polling === 'object' ? polling : { interval: polling }

  const getParams = () => (typeof params === 'function' ? params() : params)

  const formatRes = (res: ResponseData) => {
    const { data, code, msg } = res

    const formattedResult =
      formatResult?.(res, params, state.data as FormattedData) ?? data
    return { data: formattedResult, code, msg }
  }

  const handleFormatRes = (formatData: ReturnType<typeof formatRes>) => {
    const { data, code, msg } = formatData
    state.data = data
    state.code = code
    state.msg = msg
  }

  const hasCacheKey = !isNil(cacheKey)

  const getCacheKey = (params: any = getParams()) =>
    typeof cacheKey === 'function' ? cacheKey(params) : cacheKey

  // 使用缓存
  if (hasCacheKey) {
    const cacheData = getCache(getCacheKey())
    if (cacheData.startTime) {
      const res = cacheData.data as ResponseData
      const formatData = formatRes(res)
      handleFormatRes(formatData)
    }
  }

  const request = (
    params: Params<R extends RequestFunc ? R : U> = getParams(),
    cancelToken: CancelTokenSource['token']
  ) => {
    /**
     * 当一个请求没有完成又执行 run 开始另一个时，之前的会被取消，
     * 取消后在 promise 的 finally 中会把 loading 置为 false，
     * 所以需要在 finally 之后设置 loading，
     * 不然新的请求就没有 loading 态了
     *  */
    if (loadingDelay === undefined) {
      state.loading = true
    } else {
      loadingDelayTimer = setTimeout(() => {
        if (loadingDelayTimer > -1) {
          state.loading = true
        }
      }, loadingDelay)
    }

    if (pollingInterval !== undefined) {
      clearInterval(pollingTimer)
      pollingTimer = -1
    }

    const handleRes = (res: ResponseData) => {
      clearLoadingDelayTimer()

      const { data, code, msg } = formatRes(res)
      handleFormatRes({ data, code, msg })

      onSuccess?.(res, params)
      return data
    }

    const handleError = (error: RequestError) => {
      clearLoadingDelayTimer()

      state.error = error

      onError?.(error, params)

      throw error
    }

    const handleFinally = () => {
      state.loading = false

      if (pollingInterval !== undefined) {
        pollingTimer = setTimeout(() => {
          run()
        }, pollingInterval)
      }
      onEnd?.()
    }

    const realCacheKey = getCacheKey(params)

    if (hasCacheKey) {
      const cacheData = getCache(realCacheKey)
      if (cacheData.startTime) {
        if (staleTime === -1 || Date.now() - cacheData.startTime <= staleTime) {
          // 缓存是新鲜的，使用缓存数据，不发起请求
          return Promise.resolve(cacheData.data as ResponseData)
            .then(handleRes)
            .finally(handleFinally)
        }
      } else if (cacheData.pending) {
        // 若同 cacheKey 的请求发起中，则聚合请求，避免重复请求
        return getCacheByPending(realCacheKey)
          .then(res => handleRes(res as ResponseData))
          .catch(handleError)
          .finally(handleFinally)
      }
      // 发起请求，记录 pending
      setCachePending(realCacheKey)
    }

    return requestFunc(
      params,
      {
        ...config,
        cancelToken,
      },
      catcher
    )
      .then((res: ResponseData) => {
        if (hasCacheKey) {
          setCacheData(realCacheKey, cacheTime, res)
          runCacheQueue(realCacheKey, true, res)
        }

        return handleRes(res)
      })
      .catch((error: RequestError) => {
        if (hasCacheKey) {
          runCacheQueue(realCacheKey, false, error)
          removeCache(realCacheKey)
        }

        handleError(error)
      })
      .finally(handleFinally)
  }

  const normalRun = normalFn(request, state)
  const scheduleRun = debounce
    ? debounceFn(normalRun, ...normalizeScheduler<DebounceSettings>(debounce))
    : throttle
    ? throttleFn(normalRun, ...normalizeScheduler<ThrottleSettings>(throttle))
    : normalRun

  const cancel = () => {
    scheduleRun.cancel()

    clearLoadingDelayTimer()

    if (pollingInterval !== undefined) {
      clearInterval(pollingTimer)
      pollingTimer = -1
    }
  }

  const run = (
    params: Params<R extends RequestFunc ? R : U> = getParams()
  ): Promise<T> => {
    return scheduleRun(params)
  }

  if (!manual) {
    run()
  }

  onUnmounted(() => {
    cancel()
  })

  if (refreshDeps !== undefined) {
    watch<any>(
      refreshDeps === true ? getParams : refreshDeps,
      () => {
        if (refreshWhen === undefined || refreshWhen()) {
          run()
        }
      },
      {
        ...options.refreshOptions,
      }
    )
  }

  if (pollingInterval !== undefined && pollingStopWhenHidden) {
    watch(visible, value => {
      if (value && pollingTimer !== -1) {
        run()
      } else {
        cancel()
      }
    })
  }

  const removeCacheOfSelf = (key?: any) => {
    if (isNil(key) || hasCacheKey) {
      removeCache(key ?? getCacheKey())
    }
  }

  return {
    ...toRefs(state),
    run,
    cancel: cancel,
    removeCache: removeCacheOfSelf,
  }
}
